import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X as XIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  /** Canonical persisted value — what we send to the BE. */
  value: string;
  /** Label rendered in the picker. */
  label: string;
  /** Optional secondary text (e.g. region, code). */
  hint?: string;
  /** Optional small badge label (e.g. UT vs State). */
  badge?: string;
}

interface Props {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Trigger placeholder when nothing selected. */
  placeholder?: string;
  /** Tooltip when disabled / empty state copy when the list is empty. */
  emptyMessage?: string;
  /** Render chips inline below the trigger. Defaults true. */
  showChips?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Picker-driven multi-select with chip display + search. Picks are
 * persisted as `string[]` of the option `value`s. The trigger shows
 * the count + a chevron; the popover renders a searchable list of
 * checkable options. Chips below the trigger let the operator remove
 * a pick without re-opening the popover.
 *
 * Built on Radix Popover + plain Input/Button — no extra deps.
 *
 * Use this any time a form field would otherwise be free-text and the
 * domain of valid values is small + known (state codes, country
 * codes, warehouse codes, etc.). Free-text lets typos like "MA"
 * (Massachusetts? Maharashtra-typo'd?) slip in and silently break
 * downstream matching.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Pick one or more…',
  emptyMessage = 'No options to pick from.',
  showChips = true,
  disabled,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.value.toLowerCase().includes(q) ||
        o.label.toLowerCase().includes(q) ||
        (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  const selected = new Set(value);
  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? value[0]
        : `${value.length} selected`;

  const toggle = (v: string) => {
    if (selected.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || options.length === 0}
            className="w-full justify-between font-normal"
          >
            <span
              className={cn(
                'truncate',
                value.length === 0 && 'text-muted-foreground',
              )}
            >
              {summary}
            </span>
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="border-b border-border/60 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="h-8 pl-8"
                autoFocus
              />
            </div>
          </div>
          <ScrollArea className="max-h-[280px]">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                {options.length === 0 ? emptyMessage : 'No matches.'}
              </div>
            ) : (
              <div className="py-1">
                {filtered.map((o) => {
                  const isSelected = selected.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggle(o.value)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-muted/40',
                        isSelected && 'bg-primary/5',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-4 w-4 shrink-0 place-items-center rounded border',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border',
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {o.value}
                      </span>
                      <span className="flex-1 truncate text-foreground">
                        {o.label}
                      </span>
                      {o.badge && (
                        <Badge variant="muted" className="text-[9.5px]">
                          {o.badge}
                        </Badge>
                      )}
                      {o.hint && (
                        <span className="truncate text-[10.5px] text-muted-foreground">
                          {o.hint}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          {value.length > 0 && (
            <div className="flex items-center justify-between border-t border-border/60 px-2 py-1.5">
              <span className="text-[11px] text-muted-foreground">
                {value.length} selected
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] text-muted-foreground hover:text-destructive"
                onClick={() => onChange([])}
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {showChips && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <Badge
                key={v}
                variant="muted"
                className="inline-flex items-center gap-1 pr-1 font-mono text-[10.5px]"
              >
                <span>{v}</span>
                {opt && <span className="font-sans text-muted-foreground">·</span>}
                {opt && <span className="font-sans">{opt.label}</span>}
                <button
                  type="button"
                  onClick={() => onChange(value.filter((x) => x !== v))}
                  className="rounded p-0.5 hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${v}`}
                >
                  <XIcon className="h-2.5 w-2.5" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
